import React, { useState } from 'react';

// Top-tier professional / AI agent work — featured
const professionalProjects = [
  {
    title: 'ForceSight AI',
    subtitle: 'AI Finance Co-Pilot for Indian D2C Brands',
    description: 'Built the agentic engine that lets CFOs ask natural-language questions and get SKU-level margin insights, marketplace reconciliation, and inventory forecasting. The platform unifies 60+ commerce data sources and runs proactive alerts before money is lost. Users generate millions in revenue every month through this system.',
    demo: 'https://forcesight.ai/',
    tags: ['AI Agents', 'Python', 'FastAPI', 'LLMs', 'Data Pipelines'],
    highlight: 'ai',
    impact: '$1M+/mo user revenue',
  },
  {
    title: 'IndiaStox',
    subtitle: 'AI-Driven Social Platform for Indian Markets',
    description: 'The sole backend developer managing a social media platform for the Indian stock market. Designed and run all data pipelines, polls, and content flows — with AI orchestrating engagement, summarization, and trend detection across the platform.',
    demo: 'https://indiastox.com/',
    tags: ['AI Pipelines', 'Python', 'Social', 'Real-time'],
    highlight: 'ai',
    impact: 'Solo backend owner',
  },
  {
    title: 'Nexera',
    subtitle: 'AI-Native Algo-Trading Platform',
    description: 'Built the agentic backend for an institutional trading platform where users create and backtest strategies using natural language. The system researches ideas, integrates point-in-time accurate data, runs deterministic backtests, and deploys to live trading — all from a single AI-driven workflow.',
    demo: 'https://nexera.ai/',
    tags: ['AI Agents', 'Python', 'FastAPI', 'Redis', 'AWS', 'Trading'],
    highlight: 'ai',
    impact: 'Institutional grade',
  },
  {
    title: 'iHealDx',
    subtitle: 'AI Disease Diagnostic Engine',
    description: 'Built an AI agentic engine that identifies diseases through natural conversation with users — long before RAG became the industry default. The system conducts diagnostic interviews, reasons through symptoms, and surfaces likely conditions for medical review.',
    demo: 'https://ihealdx-webapp.vercel.app/',
    tags: ['AI Agents', 'NLP', 'Python', 'Healthcare'],
    highlight: 'ai',
    impact: 'Pre-RAG pioneer',
  },
  {
    title: 'FITTR',
    subtitle: 'Health, Fitness & AI Diet Tracking',
    description: 'Led multiple teams that built the FITTR web platform on a headless CMS, plus a cross-platform mobile app for tracking workouts and health metrics. Also built a separate AI-powered diet tracking app that calculates calories from food input automatically.',
    demo: 'https://www.fittr.com/',
    tags: ['Leadership', 'Headless CMS', 'Mobile', 'AI'],
    highlight: 'lead',
    impact: 'Multi-team lead',
  },
  {
    title: 'HDFC Ergo',
    subtitle: 'Modern Web Layer over Legacy CRM',
    description: 'Led the team that shipped a modern web technology stack over a legacy CRM — replacing clunky workflows with a fast, accessible interface while preserving all existing enterprise data and integrations.',
    demo: 'https://www.hdfcergo.com/',
    tags: ['Leadership', 'Modernization', 'Enterprise'],
    highlight: 'lead',
    impact: 'Legacy → modern',
  },
  {
    title: 'Indiabulls',
    subtitle: 'Trading Interface Modernization',
    description: 'Led the team responsible for migrating Indiabulls\' legacy trading interface to a modern UI — improving speed, usability, and reliability while keeping the existing trading engine intact.',
    demo: 'https://www.indiabulls.com/',
    tags: ['Leadership', 'Migration', 'Trading', 'UI'],
    highlight: 'lead',
    impact: 'Legacy → modern',
  },
];

const freelanceProjects = [
  {
    title: 'Campus Avenue (Eternosoft)',
    description: 'Helped build social media platforms for educational campuses — hubs for event highlights, hall of fame, and alumni-staff-student engagement.',
    demo: 'https://eternosoft.in/',
    tags: ['JavaScript', 'React Native', 'Google Maps'],
  },
  {
    title: 'Dextroware Devices',
    description: 'Helped build a user interface for specially-abled users to access computers — converting hardware signals into computer inputs with voice-assistance support.',
    demo: 'https://dextrowaredevices.com/',
    tags: ['C#', '.NET Core', 'Azure', 'Accessibility'],
  },
  {
    title: 'Sphark Technologies',
    description: 'Built the website and internal tools — expense management, class scheduling, and student management systems — streamlining operations end to end.',
    demo: 'https://www.spharktechnologies.com/',
    tags: ['React', 'Node.js', 'MongoDB'],
  },
  {
    title: 'ZerooCars',
    description: 'Built a comprehensive car-rental platform with booking, vehicle tracking, customer management, analytics, and reporting.',
    demo: 'https://zeroocars.com/',
    tags: ['React', 'Node.js', 'MySQL'],
  },
  {
    title: 'Ezkaam',
    description: 'Built a mobile app connecting contractors with skilled laborers, plus a VR module for immersive interior-design previews.',
    demo: 'https://www.ezkaam.in/',
    tags: ['Flutter', 'VR', 'Firebase'],
  },
  {
    title: 'Mixpanel Utility',
    description: 'Built a Mixpanel data-management utility that simplified analytics workflows and unlocked actionable insight for the client.',
    gh: 'https://github.com/Balaji2810/mix_panel_import_data_script',
    tags: ['Python', 'API', 'Analytics'],
  },
];

const personalProjects = [
  {
  title: 'Wedding E-Invite',
  description: 'A fully custom e-invitation website for our wedding — chronicling a 5-year journey from strangers to soulmates across one beautiful, scrollable page. Built with React and deployed to celebrate the big day with family and friends.',
  demo: 'https://wedding.ibhut.com/',
  tags: ['React', 'E-Invite', 'Love Story'],
  },
  {
  title: 'Super Note!',
  description: 'Note-taking app capturing audio, video, and images with seamless sharing.',
  gh: 'https://github.com/Balaji2810/SuperNote',
  tags: ['React Native', 'Firebase'],
  },
  {
  title: 'Covid-19 Tracker',
  description: 'Python scraper that aggregated daily case data, analyzed hotspots, and forecasted spread rates.',
  gh: 'https://github.com/Balaji2810/COVID-19/tree/master',
  tags: ['Python', 'Web Scraping', 'ML'],
  },
  {
  title: 'Jello Rush!',
  description: 'Endless jelly runner with advanced obstacle mechanics.',
  gh: 'https://github.com/Balaji2810',
  demo: 'https://github.com/ea9d8b06-48e8-474b-ab3b-8746c43dd79e',
  tags: ['Unity', 'C#'],
  },
  {
  title: 'Physics Runner Simulator',
  description: 'Physics-based endless runner with realistic mechanics.',
  gh: 'https://github.com/Balaji2810/1st-Game',
  demo: 'https://derg.vercel.app/',
  tags: ['Unity', 'C#', 'Physics'],
  },
];

function FeaturedCard({ project, index }) {
  const [hovered, setHovered] = useState(false);
  const isAI = project.highlight === 'ai';
  const accent = isAI ? '#00d4ff' : '#a78bfa';
  const glow = isAI ? 'rgba(0, 212, 255, 0.08)' : 'rgba(167, 139, 250, 0.06)';

  return (
    <div
      className="animate-in"
      style={{
        animationDelay: `${0.05 * index}s`,
        animationFillMode: 'both',
      }}
    >
      <div
        className="glass-card"
        style={{
          padding: '1.75rem',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          overflow: 'hidden',
          borderTop: `2px solid ${accent}`,
          transition: 'all 0.35s ease',
          transform: hovered ? 'translateY(-6px)' : 'translateY(0)',
          borderColor: hovered ? accent : 'rgba(255, 255, 255, 0.06)',
          boxShadow: hovered
            ? `0 20px 60px rgba(0, 0, 0, 0.5), 0 0 40px ${glow}`
            : 'none',
        }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        <div
          style={{
            position: 'absolute',
            top: 0,
            right: 0,
            padding: '0.3rem 0.75rem',
            fontSize: '0.65rem',
            fontWeight: 600,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            color: accent,
            background: `${accent}10`,
            borderLeft: `1px solid ${accent}30`,
            borderBottom: `1px solid ${accent}30`,
            borderBottomLeftRadius: '8px',
          }}
        >
          {isAI ? 'AI / Agent' : 'Team Lead'}
        </div>

        <div style={{ marginBottom: '0.75rem' }}>
          <h3
            style={{
              fontSize: '1.25rem',
              fontWeight: 600,
              color: '#ffffff',
              lineHeight: 1.2,
              marginBottom: '0.25rem',
            }}
          >
            {project.title}
          </h3>
          <div style={{ fontSize: '0.8rem', color: accent, fontWeight: 500 }}>
            {project.subtitle}
          </div>
        </div>

        <p
          style={{
            color: '#999',
            fontSize: '0.875rem',
            lineHeight: 1.7,
            flex: 1,
            marginBottom: '1.25rem',
          }}
        >
          {project.description}
        </p>

        {project.impact && (
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.4rem',
              padding: '0.35rem 0.75rem',
              background: `${accent}10`,
              border: `1px solid ${accent}25`,
              borderRadius: '4px',
              fontSize: '0.72rem',
              color: accent,
              fontWeight: 500,
              marginBottom: '1.25rem',
              alignSelf: 'flex-start',
            }}
          >
            <span style={{ fontSize: '0.7rem' }}>★</span>
            {project.impact}
          </div>
        )}

        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '0.4rem',
            marginBottom: '1.25rem',
          }}
        >
          {project.tags.map((tag) => (
            <span
              key={tag}
              style={{
                display: 'inline-block',
                padding: '0.2rem 0.6rem',
                background: 'rgba(255, 255, 255, 0.04)',
                border: '1px solid rgba(255, 255, 255, 0.08)',
                borderRadius: '4px',
                fontFamily: "'Inter', monospace",
                fontSize: '0.68rem',
                color: '#aaa',
                letterSpacing: '0.04em',
              }}
            >
              {tag}
            </span>
          ))}
        </div>

        <div style={{ display: 'flex', gap: '0.75rem', marginTop: 'auto' }}>
          {project.gh && (
            <a
              href={project.gh}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-accent"
              style={{
                padding: '0.5rem 1rem',
                fontSize: '0.8rem',
                borderColor: 'rgba(255, 255, 255, 0.15)',
                color: '#ccc',
              }}
            >
              &lt;/&gt; Source
            </a>
          )}
          {project.demo && (
            <a
              href={project.demo}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-accent"
              style={{
                padding: '0.5rem 1rem',
                fontSize: '0.8rem',
                borderColor: `${accent}40`,
                color: accent,
              }}
            >
              Visit →
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

function ProjectCard({ project, index }) {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      className="animate-in"
      style={{
        animationDelay: `${0.05 * index}s`,
        animationFillMode: 'both',
      }}
    >
      <div
        className="glass-card"
        style={{
          padding: '1.75rem',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          transition: 'all 0.35s ease',
          transform: hovered ? 'translateY(-6px)' : 'translateY(0)',
          borderColor: hovered ? 'rgba(0, 255, 136, 0.25)' : 'rgba(255, 255, 255, 0.06)',
          boxShadow: hovered ? '0 20px 60px rgba(0, 0, 0, 0.5), 0 0 30px rgba(0, 255, 136, 0.05)' : 'none',
        }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: '1rem',
          }}
        >
          <h3
            style={{
              fontSize: '1.1rem',
              fontWeight: 600,
              color: '#ffffff',
              lineHeight: 1.3,
            }}
          >
            {project.title}
          </h3>
          <div
            style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              background: '#00ff88',
              opacity: hovered ? 0.8 : 0.3,
              transition: 'opacity 0.3s ease',
              flexShrink: 0,
              marginTop: '6px',
            }}
          />
        </div>

        <p
          style={{
            color: '#888',
            fontSize: '0.85rem',
            lineHeight: 1.7,
            flex: 1,
            marginBottom: '1.25rem',
          }}
        >
          {project.description}
        </p>

        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '0.4rem',
            marginBottom: '1.25rem',
          }}
        >
          {project.tags.map((tag) => (
            <span
              key={tag}
              style={{
                display: 'inline-block',
                padding: '0.2rem 0.6rem',
                background: 'rgba(255, 255, 255, 0.04)',
                border: '1px solid rgba(255, 255, 255, 0.08)',
                borderRadius: '4px',
                fontFamily: "'Inter', monospace",
                fontSize: '0.68rem',
                color: '#aaa',
                letterSpacing: '0.04em',
              }}
            >
              {tag}
            </span>
          ))}
        </div>

        <div style={{ display: 'flex', gap: '0.75rem', marginTop: 'auto' }}>
          {project.gh && (
            <a
              href={project.gh}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-accent"
              style={{
                padding: '0.5rem 1rem',
                fontSize: '0.8rem',
                borderColor: 'rgba(255, 255, 255, 0.15)',
                color: '#ccc',
              }}
            >
              &lt;/&gt; Source
            </a>
          )}
          {project.demo && (
            <a
              href={project.demo}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-accent"
              style={{
                padding: '0.5rem 1rem',
                fontSize: '0.8rem',
              }}
            >
              Live →
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

export default function NewProjects() {
  return (
    <section id="projects" className="section">
      <span className="section-label animate-in">03 / Work</span>
      <h2 className="section-title animate-in animate-in-delay-1">
        Selected projects
        <br />
        I've built.
      </h2>
      <p className="section-subtitle animate-in animate-in-delay-2" style={{ marginBottom: '3rem' }}>
        From backend infrastructure to full-stack apps and games — here's what I've been working on.
      </p>

      {/* Featured / Professional */}
      <div
        className="animate-in animate-in-delay-2"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '1rem',
          marginBottom: '1.5rem',
          flexWrap: 'wrap',
        }}
      >
        <h3 style={{ fontSize: '1.1rem', color: '#00d4ff', fontWeight: 500 }}>
          AI Agents &amp; Leadership
        </h3>
        <span style={{ fontSize: '0.75rem', color: '#555' }}>
          {professionalProjects.length} projects
        </span>
      </div>
      <div className="grid-3" style={{ marginBottom: '4rem' }}>
        {professionalProjects.map((project, i) => (
          <FeaturedCard key={project.title} project={project} index={i} />
        ))}
      </div>

      {/* Freelance */}
      <h3
        className="animate-in animate-in-delay-2"
        style={{ fontSize: '1.1rem', color: '#00ff88', marginBottom: '1.5rem', fontWeight: 500 }}
      >
       Early Work
      </h3>
      <div className="grid-3" style={{ marginBottom: '4rem' }}>
        {freelanceProjects.map((project, i) => (
          <ProjectCard key={project.title} project={project} index={i} />
        ))}
      </div>

  {/* Personal & Games */}
  <h3
  className="animate-in animate-in-delay-2"
  style={{ fontSize: '1.1rem', color: '#00ff88', marginBottom: '1.5rem', fontWeight: 500 }}
  >
  Personal &amp; Games
  </h3>
  <div className="grid-3" style={{ marginBottom: '0' }}>
  {personalProjects.map((project, i) => (
  <ProjectCard key={project.title} project={project} index={i} />
  ))}
  </div>
  </section>
  );
}